import { mediaQueryLarge, requestIdleCallback } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  #paginationQueryObserver;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.#paginationQueryObserver = new MutationObserver(() => this.#preservePaginationQuery());
    this.#paginationQueryObserver.observe(this, { childList: true, subtree: true });
    this.#preservePaginationQuery();
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.#paginationQueryObserver?.disconnect();
  }

  /**
   * Shopify can omit active filter parameters from paginate URLs after a
   * filtered collection is rendered. Keep the current collection query on
   * every pagination link, including links inserted by section rendering.
   */
  #preservePaginationQuery = () => {
    const pagination = this.querySelector('nav[data-preserve-current-query]');

    if (!pagination) return;

    const currentUrl = new URL(window.location.href);
    const currentKeys = [...new Set([...currentUrl.searchParams.keys()])];

    pagination.querySelectorAll('a[href]').forEach((link) => {
      const targetUrl = new URL(link.href, currentUrl.origin);
      const targetPage = targetUrl.searchParams.get('page') || '1';

      currentKeys.forEach((key) => {
        if (key === 'page') return;

        targetUrl.searchParams.delete(key);
        currentUrl.searchParams.getAll(key).forEach((value) => {
          targetUrl.searchParams.append(key, value);
        });
      });

      targetUrl.searchParams.set('page', targetPage);
      link.href = `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`;
    });
  };

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    this.#setLayout(value);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
